import { Box, Stack, Typography, useTheme } from "@mui/material";
import AudioFile from "assets/audio/notification.aac";
import { ChatMessageDualIcon, Drawer, Tooltip } from "components";
import { useChatNotificationStore, useGlobalStore } from "controller";
import { updateChatNotificationStatus } from "controller/api/notification";
import { MqttContext } from "controller/context/MqttCreateContext";
import { useGetAllChatNotification } from "controller/hooks/notification";
import { usePinnedStore } from "controller/store/pinned-chat/pinnedChatStore";
import { useAtomValue } from "jotai";
import { useContext, useEffect, useState } from "react";
import { chatThreadParentMessage } from "shared/features/chat/store";
import { debugConsole } from "utilz/helpers";
import {
  CHAT_NOTIFICATION_EDGE_PADDING,
  getChatNotificationDrawerWidth,
  MainChatNotificationContainer,
} from "./components/MainChatNotificationContainer";

export const ChatNotificationButton = () => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const audio = new Audio(AudioFile);
  const resourceId = useGlobalStore((state) => state.resourceId);
  const mspId = useGlobalStore((state) => state.mspId);
  const selectedPinnedChatServiceId = usePinnedStore(
    (state) => state.selectedPinnedChatServiceId,
  );
  const { data: allChatNotifications } = useGetAllChatNotification({
    resourceId: resourceId || "",
    pageNumber: 1,
    perPageCount: 20,
  });

  const unseenChatNotifications = useChatNotificationStore(
    (state) => state.unseenChatNotification,
  );

  const addChatNotification = useChatNotificationStore(
    (state) => state.addChatNotification,
  );

  const resetChatNotification = useChatNotificationStore(
    (state) => state.resetAllChatNotifications,
  );
  const setChatNotificationAsSeen = useChatNotificationStore(
    (state) => state.setChatNotificationAsSeen,
  );

  const setChatNotifications = useChatNotificationStore(
    (state) => state.setChatNotifications,
  );

  const selectedChatNotification = useChatNotificationStore(
    (state) => state.selectedChatNotification,
  );
  const threadParentMessage = useAtomValue(chatThreadParentMessage);

  // The paper cannot be intrinsically sized (see MainChatNotificationContainer),
  // so mirror the container's column layout here.
  const drawerWidth = getChatNotificationDrawerWidth({
    hasSelectedChat: Boolean(selectedChatNotification),
    isThreadOpen: threadParentMessage !== null,
  });

  const { mqttMessage } = useContext(MqttContext);

  useEffect(() => {
    if (allChatNotifications) {
      setChatNotifications(allChatNotifications?.slice(0, 20));
    }
    return () => {
      resetChatNotification();
    };
  }, [allChatNotifications]);

  useEffect(() => {
    if (mqttMessage) {
      debugConsole(mqttMessage);
      if (
        mqttMessage.topic === `notification/${mspId}/all` ||
        mqttMessage.topic ===
          `notification/web_app/${mspId}/resource/${resourceId}`
      ) {
        const newMessage = JSON.parse(mqttMessage?.message);
        debugConsole(newMessage);
        if (newMessage?.result?.type === "new_chat_message") {
          handleChatNotification(newMessage?.result?.service_id, newMessage);
        }
      }
    }
  }, [mqttMessage]);

  // biome-ignore lint/suspicious/noExplicitAny: <mqtt message>
  const handleChatNotification = (ticketId: string, newMessage: any) => {
    //disable notification if pinned chat is already open
    const serviceId = newMessage?.result?.service_id;

    // get opened chat in side chat notification bar
    const selectedChatNotification =
      useChatNotificationStore.getState().selectedChatNotification;

    // if page/tab is not in focus then play sound and show notification
    if (document.hidden || !document.hasFocus()) {
      // if pinned chat is not open then play sound and show notification
      // is opened chat notification is not same as new chat notification
      if (
        serviceId !== selectedPinnedChatServiceId &&
        (!selectedChatNotification ||
          selectedChatNotification?.service_id !==
            newMessage?.result?.service_id)
      ) {
        audio.play();
        if (!window.location.href.includes(ticketId)) {
          addChatNotification(newMessage?.result);
          return;
        }
      }
      updateChatNotificationStatus({
        resourceId: resourceId || "",
        notificationId: newMessage?.result.mapping_id,
      });
      addChatNotification(newMessage?.result, true);
    } else {
      if (!window.location.href.includes(ticketId)) {
        if (
          serviceId !== selectedPinnedChatServiceId &&
          (!selectedChatNotification ||
            selectedChatNotification?.service_id !==
              newMessage?.result?.service_id)
        ) {
          addChatNotification(newMessage?.result);
          audio.play();
          return;
        }
        updateChatNotificationStatus({
          resourceId: resourceId || "",
          notificationId: newMessage?.result.mapping_id,
        });
        addChatNotification(newMessage?.result, true);
      } else {
        addChatNotification(newMessage?.result, true);
        updateChatNotificationStatus({
          resourceId: resourceId || "",
          notificationId: newMessage?.result.mapping_id,
        });
        setChatNotificationAsSeen(newMessage?.result.mapping_id);
      }
    }
  };
  return (
    <Box sx={{ position: "relative" }}>
      <Tooltip title={`You have ${unseenChatNotifications} new messages`}>
        <Stack
          onClick={() => setOpen(true)}
          sx={{
            height: "32px",
            width: "32px",
            borderRadius: "6px",
            border: `1px solid ${theme.palette.neutral["020"]}`,
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            ":hover": {
              backgroundColor: theme.palette.neutral["010"],
            },
          }}
        >
          <ChatMessageDualIcon fontSize="medium" />
        </Stack>
      </Tooltip>
      {unseenChatNotifications > 0 && (
        <Box
          minWidth={"16px"}
          height={"16px"}
          bgcolor={
            unseenChatNotifications > 0
              ? theme.palette.error.main
              : "transparent"
          }
          sx={{
            position: "absolute",
            // top: "0px",
            // right: "20px",
            top: "-4px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: unseenChatNotifications > 9 ? "8px" : "100%", // round for single digit
            px: "4px",
            zIndex: 2,
          }}
        >
          <Typography variant="caption-xs" color="#FFF" textAlign={"right"}>
            {unseenChatNotifications > 999 ? "999+" : unseenChatNotifications}
          </Typography>
        </Box>
      )}
      <Drawer
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        title="Notifications"
        footer={<></>}
        header={<></>}
        drawerStyle={{
          backgroundColor: "transparent",
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: `${drawerWidth}px`,
            maxWidth: "100vw",
            // Slide writes an inline `transition` for the transform onto this
            // node, so the width transition only lands as !important. Keep the
            // transform entry in step with the Drawer's transitionDuration.
            transition: "width 300ms ease, transform 100ms ease !important",
            WebkitTransition:
              "width 300ms ease, -webkit-transform 100ms ease !important",
          },
          "& .MuiPaper-root": {
            backgroundColor: "transparent",
            boxShadow: "none",
            // SwipeableDrawer forces ModalProps.keepMounted, so this paper stays
            // in the DOM on every page. Safari keeps compositing the
            // backdrop-filter layer even though the paper is visibility:hidden
            // and slid off-screen, so the blur bleeds over the app. Only declare
            // the filter while the drawer is actually open.
            ...(open && {
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
            }),
          },
        }}
        headerStyle={{ borderBottom: "0px" }}
      >
        <Stack
          sx={{
            height: "100%",
            overflow: "hidden",
            backgroundColor: "transparent",
            pl: `${CHAT_NOTIFICATION_EDGE_PADDING}px`,
          }}
        >
          <MainChatNotificationContainer
            onClose={() => setOpen(false)}
            isOpen={true}
          />
        </Stack>
      </Drawer>
    </Box>
  );
};
